// Channel data pooling for Float32Array reuse

import { alignedAlloc } from './aligned-buffer.js';

export class ChannelDataPool {
  private pool: Float32Array[][] = [];
  private channelCount: number = 2; // Stereo by default
  private chunkSize: number = 1024;
  private maxPoolSize: number = 20;

  private statistics = {
    allocations: 0,
    reuses: 0,
    returns: 0
  };

  /**
   * Get channel data arrays for the specified number of chunks
   */
  checkout(numChunks: number, channels: number = 2): Float32Array[][] {
    this.channelCount = channels;

    // Try to reuse from pool
    if (this.pool.length >= numChunks) {
      this.statistics.reuses += numChunks;
      return this.pool.splice(0, numChunks);
    }

    // Not enough in pool, allocate what's needed
    const result: Float32Array[][] = [];
    const fromPool = this.pool.length;

    // Use what's available from pool
    if (fromPool > 0) {
      result.push(...this.pool.splice(0, fromPool));
      this.statistics.reuses += fromPool;
    }

    // Allocate the rest with aligned memory
    const toAllocate = numChunks - fromPool;
    for (let i = 0; i < toAllocate; i++) {
      const chunk: Float32Array[] = [];
      // Use aligned allocation for better SIMD performance
      const alignedArrays = alignedAlloc.allocateFloat32Multiple(channels, this.chunkSize);
      chunk.push(...alignedArrays);
      result.push(chunk);
      this.statistics.allocations++;
    }

    return result;
  }

  /**
   * Return channel data arrays to the pool
   */
  checkin(channelDatas: Float32Array[][]): void {
    this.statistics.returns += channelDatas.length;

    // Only keep up to maxPoolSize
    const spaceAvailable = this.maxPoolSize - this.pool.length;
    if (spaceAvailable > 0) {
      const toAdd = Math.min(spaceAvailable, channelDatas.length);
      this.pool.push(...channelDatas.slice(0, toAdd));
    }
  }

  /**
   * Fill channel data from audio buffer into pre-allocated arrays
   */
  fillFromAudioBuffer(
    channelDatas: Float32Array[][],
    audioBuffer: AudioBuffer,
    requestedStart: number,
    requestedEnd: number,
    clampedStart: number
  ): void {
    let dataIndex = 0;
    for (let i = requestedStart; i < requestedEnd; i++) {
      const chunk = channelDatas[dataIndex];
      for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
        const sourceData = audioBuffer.getChannelData(ch);
        const startOffset = (i - clampedStart) * 1024;
        const endOffset = startOffset + 1024;

        // Copy data into pre-allocated array
        chunk[ch].set(sourceData.subarray(startOffset, endOffset));
      }
      dataIndex++;
    }
  }

  /**
   * Get statistics
   */
  getStatistics() {
    const total = this.statistics.allocations + this.statistics.reuses;
    return {
      ...this.statistics,
      poolSize: this.pool.length,
      reuseRate: total > 0 ? ((this.statistics.reuses / total) * 100).toFixed(2) + '%' : '0%',
      memoryInPool: this.pool.length * this.channelCount * this.chunkSize * 4 // 4 bytes per float
    };
  }

  /**
   * Clear the pool
   */
  clear(): void {
    this.pool = [];
  }
}

// Global instance
export const channelDataPool = new ChannelDataPool();
